import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField as MuiTextField,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React from "react";
import { useTranslate } from "react-admin";

const COLUMNS = [
	{ source: "ext_id", label: "tab.pallets.external_id", type: "digits" },
	{ source: "quantity", label: "tab.pallets.quantity", type: "number", min: 0 },
	{ source: "net_weight", label: "tab.pallets.weight_net", type: "number", min: 0 },
	{
		source: "unit_weights",
		label: "osfc.dialogs.generate-pallets-from-pdf.unit_weights",
		readOnly: true,
	},
	{ source: "product_number", label: "tab.pallets.product_number" },
	{ source: "product_name", label: "tab.pallets.product_name" },
	{ source: "lot_number", label: "tab.pallets.lot_number" },
	{ source: "packing_station", label: "tab.pallets.packing_station" },
	{ source: "production_datetime", label: "tab.pallets.production_datetime" },
	{ source: "expiration_datetime", label: "tab.pallets.expiration_datetime" },
	{ source: "sscc", label: "tab.pallets.sscc" },
];

const useStyles = makeStyles(() => ({
	cellInput: { minWidth: 90 },
	idCellInput: { minWidth: 180 },
	wideCellInput: { minWidth: 220 },
	tableWrapper: { overflowX: "auto", maxHeight: "60vh" },
}));

const toNumberOrNull = (value) => {
	if (value === "" || value === null || value === undefined) return null;
	const parsed = Number(value);
	return Number.isNaN(parsed) ? value : parsed;
};

// A pallet number read off an SSCC column is 18 digits, which a JS number cannot hold
// exactly, so it is edited and sent as text and the backend parses it back.
const toDigitsOrNull = (value) => value.replace(/\D/g, "") || null;

const toTextOrNull = (value) => value || null;

const CELL_TYPES = {
	number: {
		inputType: "number",
		parse: toNumberOrNull,
		inputClass: "cellInput",
		inputProps: (column) => ({ min: column.min }),
	},
	digits: {
		inputType: "text",
		parse: toDigitsOrNull,
		inputClass: "idCellInput",
		inputProps: () => ({ inputMode: "numeric" }),
	},
};

const TEXT_CELL_TYPE = {
	inputType: "text",
	parse: toTextOrNull,
	inputClass: "wideCellInput",
	inputProps: () => ({}),
};

const PreviewCell = ({ row, column, onChange }) => {
	const classes = useStyles();

	if (column.readOnly) {
		return (
			<TableCell title={row[column.source] || ""}>
				<Typography variant="body2" noWrap style={{ maxWidth: 260 }}>
					{row[column.source] || "—"}
				</Typography>
			</TableCell>
		);
	}

	const cellType = CELL_TYPES[column.type] || TEXT_CELL_TYPE;

	return (
		<TableCell>
			<MuiTextField
				className={classes[cellType.inputClass]}
				value={row[column.source] === null || row[column.source] === undefined ? "" : row[column.source]}
				type={cellType.inputType}
				inputProps={cellType.inputProps(column)}
				onChange={(e) => onChange(row.id, column.source, cellType.parse(e.target.value))}
			/>
		</TableCell>
	);
};

export const PreviewRowsTable = ({ rows, onCellChange }) => {
	const classes = useStyles();
	const translate = useTranslate();

	if (rows.length === 0) {
		return (
			<Typography variant="body2">
				{translate("osfc.dialogs.generate-pallets-from-pdf.no_rows")}
			</Typography>
		);
	}

	return (
		<div className={classes.tableWrapper}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						{COLUMNS.map((column) => (
							<TableCell key={column.source}>{translate(column.label)}</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.id}>
							{COLUMNS.map((column) => (
								<PreviewCell
									key={column.source}
									row={row}
									column={column}
									onChange={onCellChange}
								/>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
};
